"use client";

import { useEffect, useState } from "react";
import { getCurrentDateUTC } from "@/lib/daily/dateUtils";
import { getAbsoluteDayIndex, getWordForDate } from "@/lib/daily/contentSelector";
import { FinWordContent } from "@/lib/daily/types";
import FinWordGame from "@/components/daily/word/FinWordGame";
import DailyLoader from "@/components/daily/shared/DailyLoader";
import { purgePracticeStates } from "@/lib/daily/storageManager";
import type { YMALPoolItem } from "@/lib/blog/ymal-mapper";
import wordsData from "@/data/daily/words.json";

// Helper to get a random word that hasn't been played recently
function getRandomWord(recentIds: number[]): FinWordContent {
  // Filter out recently played words
  const availableWords = wordsData.filter(w => !recentIds.includes(w.id));
  
  // If all words have been played recently, reset the history
  const pool = availableWords.length > 0 ? availableWords : wordsData;
  
  const randomIndex = Math.floor(Math.random() * pool.length);
  return pool[randomIndex] as FinWordContent;
}

export default function FinWordPageClient({ ymalPool }: { ymalPool: YMALPoolItem[] }) {
  const [data, setData] = useState<{ date: string; content: FinWordContent; dayNum: number; mode: "daily" | "practice" } | null>(null);

  useEffect(() => {
    // Check if we're in practice mode
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    const isPracticeMode = mode === "practice";

    if (isPracticeMode) {
      // Clean up state keys left behind by previous practice sessions
      purgePracticeStates();

      // Generate a random practice ID
      const practiceId = `practice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Get recent practice word IDs from localStorage (guarded — a corrupted
      // value must not crash the page into a permanent loader)
      const recentKey = "finword_practice_recent";
      let recentIds: number[] = [];
      try {
        const recentStr = localStorage.getItem(recentKey);
        const parsed = recentStr ? JSON.parse(recentStr) : [];
        if (Array.isArray(parsed)) recentIds = parsed;
      } catch {
        recentIds = [];
      }
      
      // Select a random word avoiding recent ones
      const content = getRandomWord(recentIds);
      
      // Update recent history (keep last 20 words)
      const updatedRecent = [content.id, ...recentIds].slice(0, 20);
      localStorage.setItem(recentKey, JSON.stringify(updatedRecent));
      
      setData({ date: practiceId, content, dayNum: 0, mode: "practice" });
    } else {
      // Daily mode
      const date = getCurrentDateUTC();
      const content = getWordForDate(date);
      const dayNum = getAbsoluteDayIndex(date) + 1;
      setData({ date, content, dayNum, mode: "daily" });
    }
  }, []);

  if (!data) {
    return <DailyLoader message="Loading today's puzzle..." />;
  }

  return (
    <>
      <h1 className="sr-only">FinWord Puzzle #{data.dayNum}</h1>
      <FinWordGame content={data.content} date={data.date} mode={data.mode} ymalPool={ymalPool} />
    </>
  );
}
